/**
 * Roster print sheet — the week by line, stripped for paper.
 *
 * Mounted only while printing. The app's print stylesheet hides everything
 * but `.print-target`, so this is the whole page: plant and week in the
 * header, one row per work center with work, one column per day. In each
 * cell the day's work is folded to ONE block per product (the 1st- and
 * 2nd-shift steps of the same product share it), under which each
 * operation lists its people one per line — the names are the loudest text
 * on the page, each with a small shift tag — and every unfilled crew line
 * prints as a blank slot so the gaps show on paper too. No metrics, no
 * bench, no controls. "out" after a name is a confirmed absence, "time
 * off?" a pending request.
 */
import { format } from "date-fns";
import React, { useMemo } from "react";

import { ShiftInstance } from "../../../../../services/shifts-api";
import { availabilityBadge } from "../utils/availability";
import { groupCrewLines, LaneCard, LaneCrewGroup, LaneStep } from "../utils/lane-rows"; // eslint-disable-line sort-imports
import { shiftLabelFor, WeekLane, WeekLaneDay, WeekLanes } from "../utils/week-lanes"; // eslint-disable-line sort-imports

const INK = "#121826";
const MUTED = "#6B7390";
const BRICK = "#9A2C22";
const RULE = "1px solid #B8BDCB";
const HAIR = "1px solid #E1E4EC";
const FONT = "'Inter', 'Trebuchet MS', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace";

const SCREEN_HIDE = "@media screen { .roster-print-sheet { display: none !important; } }";

/** Product names longer than this are cut with an ellipsis so a header stays on one line. */
const PRODUCT_MAX = 26;

const cell: React.CSSProperties = {
	border: HAIR,
	padding: "5px 6px",
	verticalAlign: "top",
	fontFamily: FONT,
	fontSize: 9,
	lineHeight: 1.25,
	color: INK,
};

const shortProduct = (title: string): string => {
	const clean = title.replace(/\s+/g, " ").trim();
	if (clean.length <= PRODUCT_MAX) {
		return clean;
	}
	// Cut at the last word boundary that still fits, so "Mixed Amphet. Salts Caps…" not "Mixed Amphet. Salts C…".
	const head = clean.slice(0, PRODUCT_MAX - 1);
	const space = head.lastIndexOf(" ");
	return `${(space > PRODUCT_MAX / 2 ? head.slice(0, space) : head).replace(/[\s.,·-]+$/, "")}…`;
};

/** "1st" / "2nd" / a pattern name, as the day view labels it. */
const shiftOf = (startHour: number, shiftId: string | null | undefined, shifts: ShiftInstance[]): string =>
	shiftLabelFor(shiftId, startHour, shifts);

interface PrintPerson {
	key: string;
	shift: string;
	name: string;
	/** "out" / "time off?" — null when expected. */
	mark: string | null;
	markTone: "danger" | "warning" | null;
}

interface PrintOperation {
	key: string;
	label: string;
	have: number;
	needed: number;
	people: PrintPerson[];
	/** One entry per unfilled crew line, each the shift it falls in. */
	openShifts: string[];
}

interface PrintBlock {
	key: string;
	product: string;
	operations: PrintOperation[];
}

const personOf = (card: LaneCard, shifts: ShiftInstance[]): PrintPerson => {
	const shift = shiftOf(card.start, card.shiftId, shifts);
	const badge = card.unavailable ? availabilityBadge(card.unavailable) : null;
	return {
		key: `${shift}::${card.workerId}`,
		shift,
		name: card.workerName,
		mark: badge ? badge.text.toLowerCase() : null,
		markTone: badge ? badge.tone : null,
	};
};

/** Role shown only when it is not the operation's obvious role (two roles on one operation, or a role that names something the operation does not). */
const operationLabel = (group: LaneCrewGroup, rolesOnOperation: number): string => {
	if (!group.jobRole || rolesOnOperation <= 1) {
		return group.operationName;
	}
	return `${group.operationName} · ${group.jobRole}`;
};

/** The day's steps folded into product blocks; within a block, one entry per operation (+ role when needed). */
function foldDay(steps: LaneStep[], loose: LaneCard[], shifts: ShiftInstance[]): PrintBlock[] {
	const blocks = new Map<string, PrintBlock>();
	const ensureBlock = (product: string): PrintBlock => {
		let block = blocks.get(product);
		if (!block) {
			block = { key: product, product, operations: [] };
			blocks.set(product, block);
		}
		return block;
	};
	const ensureOperation = (block: PrintBlock, key: string, label: string): PrintOperation => {
		let operation = block.operations.find((o) => o.key === key);
		if (!operation) {
			operation = { key, label, have: 0, needed: 0, people: [], openShifts: [] };
			block.operations.push(operation);
		}
		return operation;
	};
	const addPeople = (operation: PrintOperation, cards: LaneCard[]): void => {
		cards.forEach((card) => {
			const person = personOf(card, shifts);
			if (!operation.people.some((p) => p.key === person.key)) {
				operation.people.push(person);
			}
		});
	};

	steps.forEach((step) => {
		const block = ensureBlock(step.title);
		const groups = groupCrewLines(step.phases);
		const rolesPerOperation = new Map<string, number>();
		groups.forEach((g) => rolesPerOperation.set(g.operationName, (rolesPerOperation.get(g.operationName) ?? 0) + 1));
		groups.forEach((group) => {
			const label = operationLabel(group, rolesPerOperation.get(group.operationName) ?? 1);
			const operation = ensureOperation(block, group.key, label);
			operation.have += group.have;
			operation.needed += group.needed;
			addPeople(operation, group.cards);
			group.slots.forEach((slot) => operation.openShifts.push(shiftOf(slot.start, null, shifts)));
		});
	});
	if (loose.length) {
		const block = ensureBlock("Other");
		const operation = ensureOperation(block, "other", "Unplanned");
		addPeople(operation, loose);
		operation.have = operation.people.length;
		operation.needed = operation.people.length;
	}
	// People sorted by shift then name so both shifts read as two tidy runs.
	blocks.forEach((block) =>
		block.operations.forEach((operation) =>
			operation.people.sort((a, b) => a.shift.localeCompare(b.shift) || a.name.localeCompare(b.name))
		)
	);
	return Array.from(blocks.values());
}

const ShiftTag: React.FC<{ shift: string }> = ({ shift }) => (
	<span
		style={{
			display: "inline-block",
			width: 22,
			flexShrink: 0,
			fontFamily: MONO,
			fontSize: 7.5,
			color: MUTED,
			letterSpacing: "0.02em",
		}}
	>
		{shift}
	</span>
);

const PersonLine: React.FC<{ person: PrintPerson }> = ({ person }) => (
	<div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
		<ShiftTag shift={person.shift} />
		<span style={{ fontSize: 10, fontWeight: 700, color: person.markTone === "danger" ? MUTED : INK }}>
			{person.name}
		</span>
		{person.mark && (
			<span style={{ fontSize: 8, fontStyle: "italic", color: person.markTone === "danger" ? BRICK : "#8A5A12", whiteSpace: "nowrap" }}>
				{person.mark}
			</span>
		)}
	</div>
);

const OpenLine: React.FC<{ shift: string }> = ({ shift }) => (
	<div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
		<ShiftTag shift={shift} />
		<span
			aria-label="open crew line"
			style={{ flex: 1, borderBottom: `1px dotted ${BRICK}`, height: 10, maxWidth: 110 }}
		/>
		<span style={{ fontSize: 8, fontStyle: "italic", color: BRICK, whiteSpace: "nowrap" }}>open</span>
	</div>
);

const OperationBlock: React.FC<{ operation: PrintOperation }> = ({ operation }) => (
	<div style={{ marginTop: 3 }}>
		<div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
			<span style={{ fontSize: 8, color: MUTED }}>{operation.label}</span>
			<span
				style={{
					fontFamily: MONO,
					fontSize: 8,
					whiteSpace: "nowrap",
					color: operation.have < operation.needed ? BRICK : MUTED,
					fontWeight: operation.have < operation.needed ? 700 : 400,
				}}
			>
				{operation.have}/{operation.needed}
			</span>
		</div>
		{operation.people.map((person) => (
			<PersonLine key={person.key} person={person} />
		))}
		{operation.openShifts.map((shift, index) => (
			// Slots have no identity of their own; position is the only stable key.
			// eslint-disable-next-line react/no-array-index-key
			<OpenLine key={`${shift}-${index}`} shift={shift} />
		))}
	</div>
);

const ProductBlock: React.FC<{ block: PrintBlock; last: boolean }> = ({ block, last }) => (
	<div className="no-break" style={{ paddingBottom: last ? 0 : 6, marginBottom: last ? 0 : 6, borderBottom: last ? "none" : HAIR }}>
		<div style={{ fontSize: 9.5, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={block.product}>
			{block.product}
		</div>
		{block.operations.map((operation) => (
			<OperationBlock key={operation.key} operation={operation} />
		))}
	</div>
);

const DayCell: React.FC<{ day: WeekLaneDay; shifts: ShiftInstance[] }> = ({ day, shifts }) => {
	const blocks = useMemo(
		() => foldDay(day.steps, day.loose, shifts).map((block) => ({ ...block, product: shortProduct(block.product) })),
		[day.steps, day.loose, shifts]
	);
	if (!blocks.length) {
		return <td style={{ ...cell, background: "#FAFBFC" }} />;
	}
	return (
		<td style={cell}>
			{blocks.map((block, index) => (
				<ProductBlock key={block.key} block={block} last={index === blocks.length - 1} />
			))}
		</td>
	);
};

const LaneRow: React.FC<{ lane: WeekLane; shifts: ShiftInstance[] }> = ({ lane, shifts }) => (
	<tr>
		<th scope="row" style={{ ...cell, textAlign: "left", width: 92, background: "#F4F5F8" }}>
			<div style={{ fontSize: 10, fontWeight: 700 }}>{lane.workcenter}</div>
			<div style={{ fontFamily: MONO, fontSize: 8, color: MUTED }}>{Math.round(lane.weekHours)}h</div>
		</th>
		{lane.days.map((day) => (
			<DayCell key={day.dayIso} day={day} shifts={shifts} />
		))}
	</tr>
);

export interface RosterPrintSheetProps {
	lanes: WeekLanes;
	shifts: ShiftInstance[];
	weekStart: Date;
	plantName?: string;
}

const RosterPrintSheet: React.FC<RosterPrintSheetProps> = ({ lanes, shifts, weekStart, plantName }) => {
	const rows = useMemo(() => lanes.lanes.filter((lane) => !lane.idle), [lanes.lanes]);
	const last = lanes.days[lanes.days.length - 1]?.date ?? weekStart;
	return (
		<>
			<style>{SCREEN_HIDE}</style>
			<div className="print-target roster-print-sheet" style={{ padding: 16, background: "#FFFFFF", color: INK }}>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "baseline",
						borderBottom: RULE,
						paddingBottom: 6,
						marginBottom: 8,
					}}
				>
					<div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700 }}>Roster{plantName ? ` · ${plantName}` : ""}</div>
					<div style={{ fontFamily: MONO, fontSize: 10, color: MUTED }}>
						{format(weekStart, "EEE d MMM")} – {format(last, "EEE d MMM yyyy")} · printed {format(new Date(), "d MMM yyyy HH:mm")}
					</div>
				</div>
				<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
					<thead>
						<tr>
							<th style={{ ...cell, textAlign: "left", width: 92, background: "#F4F5F8", borderBottom: RULE }}>Line</th>
							{lanes.days.map((day) => (
								<th key={day.dayIso} style={{ ...cell, textAlign: "left", background: "#F4F5F8", borderBottom: RULE }}>
									<div style={{ fontSize: 10, fontWeight: 700 }}>{format(day.date, "EEE d")}</div>
									<div style={{ fontFamily: MONO, fontSize: 8, color: MUTED, fontWeight: 400 }}>
										{day.needed > 0 ? `${day.have}/${day.needed} crew` : "no work"}
										{day.outCount > 0 ? ` · ${day.outCount} out` : ""}
									</div>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((lane) => (
							<LaneRow key={lane.workcenter} lane={lane} shifts={shifts} />
						))}
						{rows.length === 0 && (
							<tr>
								<td colSpan={lanes.days.length + 1} style={{ ...cell, color: MUTED, padding: 12 }}>
									No work planned this week.
								</td>
							</tr>
						)}
					</tbody>
				</table>
				<div style={{ marginTop: 8, fontFamily: FONT, fontSize: 8, color: MUTED }}>
					One block per product, both shifts together · 1st / 2nd = shift · n/n = people on the operation over people needed · dotted line = crew line still open · &ldquo;out&rdquo; = confirmed absence or time off · &ldquo;time off?&rdquo; = request pending
				</div>
			</div>
		</>
	);
};

export default RosterPrintSheet;
